import { useState } from 'react';
import {
  Callout,
  Field,
  Loading,
  Select,
  TextInput,
  isApiClientError,
  useLoad,
} from '@affdice/portal-kit';

import type { PlatformApi } from '../../client.ts';
import { t } from '../../messages.ts';
import { DirectEditDialog } from '../DirectEditDialog.tsx';
import {
  affiliateStatusTargets,
  isFinalStatus,
  parseTags,
  profileChanges,
  profileDraft,
  tagsChanged,
  PROFILE_FIELDS,
  type ProfileDraft,
  type ProfileField,
} from '../direct-edit-model.ts';
import type { AffiliateEdit } from '../entity-actions.ts';
import { Failure } from '../shared.tsx';

/**
 * Direct edits on one affiliate profile (Rule Book 3.9d): status, the
 * operator's own fields, tags, manager and a note. Each is the operator's own
 * route, called inside that operator with the reason the dialog asks for;
 * the server runs the same checks it runs for the operator's staff, audits
 * the change under the platform user and tells the operator.
 */
export interface AffiliateEditTarget {
  readonly operatorId: string;
  readonly affiliateId: string;
  readonly name: string;
  readonly status: string;
  readonly fields: Parameters<typeof profileDraft>[0];
  readonly tags: readonly string[];
  readonly managers: readonly { role: string; manager_id: string; manager: string }[];
}

export function AffiliateEditDialog({
  api,
  edit,
  target,
  onClose,
  onDone,
}: {
  api: PlatformApi;
  edit: AffiliateEdit;
  target: AffiliateEditTarget;
  onClose: () => void;
  /** Accepted: `note` says what changed. */
  onDone: (note: string) => void;
}) {
  switch (edit) {
    case 'edit-status': return <StatusEdit api={api} target={target} onClose={onClose} onDone={onDone} />;
    case 'edit-profile': return <ProfileEdit api={api} target={target} onClose={onClose} onDone={onDone} />;
    case 'edit-tags': return <TagsEdit api={api} target={target} onClose={onClose} onDone={onDone} />;
    case 'edit-manager': return <ManagerEdit api={api} target={target} onClose={onClose} onDone={onDone} />;
    case 'add-note': return <NoteAdd api={api} target={target} onClose={onClose} onDone={onDone} />;
  }
}

type EditProps = {
  api: PlatformApi;
  target: AffiliateEditTarget;
  onClose: () => void;
  onDone: (note: string) => void;
};

/** The label of an affiliate status, as the operator's console names it. */
export function statusLabel(status: string): string {
  switch (status) {
    case 'ACTIVE': return t('edit.status.ACTIVE');
    case 'UNDER_REVIEW': return t('edit.status.UNDER_REVIEW');
    case 'SUSPENDED': return t('edit.status.SUSPENDED');
    case 'BLOCKED': return t('edit.status.BLOCKED');
    case 'TERMINATED': return t('edit.status.TERMINATED');
    case 'DEACTIVATED': return t('edit.status.DEACTIVATED');
    case 'PENDING': return t('edit.status.PENDING');
    case 'REJECTED': return t('edit.status.REJECTED');
    default: return status;
  }
}

function StatusEdit({ api, target, onClose, onDone }: EditProps) {
  const targets = affiliateStatusTargets(target.status);
  const [to, setTo] = useState(targets[0] ?? '');
  // 6.9 — deactivating over a payable balance is refused until forced with a
  // justification; the refusal itself (the server's words) shows above.
  const [blocked, setBlocked] = useState(false);
  const [force, setForce] = useState(false);
  const [justification, setJustification] = useState('');

  return (
    <DirectEditDialog
      title={t('edit.status.title', { name: target.name })}
      submitLabel={t('edit.status.submit', { to: statusLabel(to) })}
      danger={['BLOCKED', 'TERMINATED', 'REJECTED', 'SUSPENDED', 'DEACTIVATED'].includes(to)}
      ready={to !== '' && (!blocked || !force || justification.trim() !== '')}
      run={(reason) =>
        api
          .changeAffiliateStatusDirect(target.operatorId, target.affiliateId, {
            to,
            reasonCode: reason,
            ...(blocked && force ? { force: true, justification: justification.trim() } : {}),
          }, reason)
          .catch((e: unknown) => {
            if (isApiClientError(e) && e.code === 'PENDING_BALANCE') setBlocked(true);
            throw e;
          })}
      onClose={onClose}
      onDone={() => onDone(t('edit.status.done', { name: target.name, to: statusLabel(to) }))}
    >
      <p>{t('edit.status.now', { status: statusLabel(target.status) })}</p>
      <div className="form-row">
        <Field label={t('edit.status.to')}>
          <Select
            value={to}
            onChange={(v) => { setTo(v); setBlocked(false); setForce(false); }}
            options={targets.map((s) => ({ value: s, label: statusLabel(s) }))}
          />
        </Field>
      </div>
      {isFinalStatus(to) ? <Callout tone="caution">{t('edit.status.final')}</Callout> : null}
      {/* Recorded as the platform's: the operator cannot undo it from its own
          console (review, 2026-10-07). */}
      {to === 'DEACTIVATED' ? <Callout tone="caution">{t('edit.status.platformDeactivation')}</Callout> : null}
      {to === 'ACTIVE' && (target.status === 'BLOCKED' || target.status === 'TERMINATED') ? (
        <Callout tone="caution">{t('edit.status.reinstate')}</Callout>
      ) : null}
      {blocked ? (
        <>
          <label className="checkbox-stack-row">
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
            {' '}{t('edit.status.force')}
          </label>
          {force ? (
            <Field label={t('edit.status.justification')}>
              <TextInput value={justification} onChange={setJustification} />
            </Field>
          ) : null}
        </>
      ) : null}
    </DirectEditDialog>
  );
}

const PROFILE_LABEL: Record<ProfileField | 'geos', Parameters<typeof t>[0]> = {
  username: 'edit.profile.username',
  external_affiliate_id: 'edit.profile.externalId',
  company_name: 'edit.profile.company',
  contact_name: 'edit.profile.contact',
  phone: 'edit.profile.phone',
  website_urls: 'edit.profile.websites',
  marketing_note: 'edit.profile.marketing',
  country: 'edit.profile.country',
  geos: 'edit.profile.geos',
};

function ProfileEdit({ api, target, onClose, onDone }: EditProps) {
  const original = profileDraft(target.fields);
  const [draft, setDraft] = useState<ProfileDraft>(original);
  const changes = profileChanges(original, draft);
  const set = (f: keyof ProfileDraft) => (v: string) => setDraft((d) => ({ ...d, [f]: v }));

  return (
    <DirectEditDialog
      wide
      title={t('edit.profile.title', { name: target.name })}
      submitLabel={t('edit.saveNow')}
      ready={changes !== null}
      run={(reason) => api.setAffiliateProfileFields(target.operatorId, target.affiliateId, changes ?? {}, reason)}
      onClose={onClose}
      onDone={() => onDone(t('edit.profile.done', { name: target.name }))}
    >
      <div className="form-row">
        {[...PROFILE_FIELDS, 'geos' as const].map((f) => (
          <Field
            key={f}
            label={t(PROFILE_LABEL[f])}
            {...(f === 'geos' ? { hint: t('edit.profile.geosHint') } : f === 'country' ? { hint: t('edit.profile.countryHint') } : {})}
          >
            <TextInput value={draft[f]} onChange={set(f)} />
          </Field>
        ))}
      </div>
      <p className="faint">{t('edit.profile.onlyChanged')}</p>
    </DirectEditDialog>
  );
}

function TagsEdit({ api, target, onClose, onDone }: EditProps) {
  const [text, setText] = useState(target.tags.join(', '));
  const next = parseTags(text);
  return (
    <DirectEditDialog
      title={t('edit.tags.title', { name: target.name })}
      submitLabel={t('edit.saveNow')}
      ready={tagsChanged(target.tags, next)}
      run={(reason) => api.setAffiliateTags(target.operatorId, target.affiliateId, next, reason)}
      onClose={onClose}
      onDone={() => onDone(t('edit.tags.done', { name: target.name }))}
    >
      <Field label={t('edit.tags.field')} hint={t('edit.tags.hint')}>
        <TextInput value={text} onChange={setText} placeholder={t('edit.tags.placeholder')} />
      </Field>
      {next.length === 0 && target.tags.length > 0 ? (
        <Callout tone="caution">{t('edit.tags.clearing')}</Callout>
      ) : null}
    </DirectEditDialog>
  );
}

function ManagerEdit({ api, target, onClose, onDone }: EditProps) {
  // The operator's own team: a manager is one of its people (9.2a). A logged
  // read, made when this dialog opens rather than with the popup.
  const team = useLoad(() => api.operatorTeam(target.operatorId), [target.operatorId]);
  const [managerId, setManagerId] = useState('');
  const [role, setRole] = useState<'PRIMARY' | 'SECONDARY'>('PRIMARY');
  const current = target.managers.find((m) => m.role === role);
  const people = team.state === 'ready'
    ? team.value.items.filter((m) => m.status === 'ACTIVE')
    : [];

  return (
    <DirectEditDialog
      title={t('edit.manager.title', { name: target.name })}
      submitLabel={t('edit.manager.submit')}
      ready={managerId !== '' && managerId !== current?.manager_id}
      run={(reason) =>
        api.assignAffiliateManager(target.operatorId, target.affiliateId, { managerId, role }, reason)}
      onClose={onClose}
      onDone={() => {
        const who = people.find((p) => p.id === managerId);
        onDone(t('edit.manager.done', { name: target.name, manager: who?.name || who?.email || '' }));
      }}
    >
      <p>
        {target.managers.length === 0
          ? t('edit.manager.none')
          : target.managers.map((m) => t('edit.manager.current', {
            role: m.role === 'PRIMARY' ? t('edit.manager.primary') : t('edit.manager.secondary'),
            manager: m.manager,
          })).join(' · ')}
      </p>
      {team.state === 'loading' ? <Loading rows={1} /> : null}
      {team.state === 'error' ? <Failure error={team.error} /> : null}
      {team.state === 'ready' ? (
        people.length === 0 ? (
          <Callout tone="caution">{t('edit.manager.noTeam')}</Callout>
        ) : (
          <div className="form-row">
            <Field label={t('edit.manager.person')}>
              <Select
                value={managerId}
                onChange={setManagerId}
                options={[
                  { value: '', label: t('edit.manager.choose') },
                  ...people.map((p) => ({ value: p.id, label: p.name ? `${p.name} (${p.email})` : p.email })),
                ]}
              />
            </Field>
            <Field label={t('edit.manager.role')}>
              <Select
                value={role}
                onChange={(v) => setRole(v as 'PRIMARY' | 'SECONDARY')}
                options={[
                  { value: 'PRIMARY', label: t('edit.manager.primary') },
                  { value: 'SECONDARY', label: t('edit.manager.secondary') },
                ]}
              />
            </Field>
          </div>
        )
      ) : null}
      <p className="faint">{t('edit.manager.fromNow')}</p>
    </DirectEditDialog>
  );
}

function NoteAdd({ api, target, onClose, onDone }: EditProps) {
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState<'INTERNAL' | 'AFFILIATE_VISIBLE'>('INTERNAL');
  return (
    <DirectEditDialog
      title={t('edit.note.title', { name: target.name })}
      submitLabel={t('edit.note.submit')}
      ready={body.trim() !== ''}
      run={(reason) =>
        api.addAffiliateNote(target.operatorId, target.affiliateId, { body: body.trim(), visibility }, reason)}
      onClose={onClose}
      onDone={() => onDone(t('edit.note.done', { name: target.name }))}
    >
      <Field label={t('edit.note.body')}>
        <textarea
          className="control"
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </Field>
      <Field label={t('edit.note.visibility')}>
        <Select
          value={visibility}
          onChange={(v) => setVisibility(v as 'INTERNAL' | 'AFFILIATE_VISIBLE')}
          options={[
            { value: 'INTERNAL', label: t('edit.note.internal') },
            { value: 'AFFILIATE_VISIBLE', label: t('edit.note.visible') },
          ]}
        />
      </Field>
    </DirectEditDialog>
  );
}
